import React, { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TwitterIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShareIcon from '@mui/icons-material/Share';
import Fade from '@mui/material/Fade';

const ShareMenu = ({ articleUrl, articleTitle, iconVariant = 'default', onShared, compact = false }) => {
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 640px)').matches);

  useEffect(() => {
    const handler = () => setIsMobile(window.matchMedia('(max-width: 640px)').matches);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const notifyShared = () => {
    onShared?.();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(articleUrl);
      setCopied(true);
      setSnackbarOpen(true);
      notifyShared();
      handleClose();

      // Reset animation after 1.5s
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const encodedUrl = encodeURIComponent(articleUrl);
  const encodedTitle = encodeURIComponent(articleTitle || 'Check out this news article!');

  return (
    <>
      <Tooltip title="Share this article" arrow>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
          size={compact ? 'small' : 'large'}
          style={{
            background: 'linear-gradient(135deg, #E0EDFF, #F3F9FF)',
            borderRadius: compact ? 12 : 16,
            padding: compact ? 6 : 8,
            width: compact ? 36 : undefined,
            height: compact ? 36 : undefined,
            transition: 'all 0.25s ease',
            boxShadow: compact ? '0 2px 8px rgba(0,0,0,0.08)' : '0 4px 10px rgba(0,0,0,0.08)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 16px 0 rgba(0,0,0,0.10)';
          }}
        >
          {copied ? (
            <CheckCircleIcon
              style={{
                color: '#4caf50',
                fontSize: compact ? 18 : 24,
                animation: 'bounce 0.6s ease',
              }}
            />
          ) : (
            iconVariant === 'apple' ? (
              <svg
                width={isMobile ? "22" : "28"}
                height={isMobile ? "22" : "28"}
                viewBox="0 0 24 24"
                fill="none"
                style={{ display: 'block' }}
              >
                <defs>
                  <linearGradient id="appleBlue" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#4F8CFF" />
                    <stop offset="1" stopColor="#007AFF" />
                  </linearGradient>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#007AFF" floodOpacity="0.12" />
                  </filter>
                </defs>
                <rect x="4.5" y="14.5" width="15" height="5" rx="2.5" fill="#fff" stroke="url(#appleBlue)" strokeWidth="1.5" filter="url(#shadow)" />
                <path d="M12 15V6" stroke="url(#appleBlue)" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M8.5 9.5L12 6l3.5 3.5" stroke="url(#appleBlue)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <ShareIcon style={{ color: '#374151', fontSize: compact ? 18 : 24 }} />
            )
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        transitionDuration={200}
        anchorOrigin={isMobile ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={isMobile ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            minWidth: isMobile ? 140 : 180,
            maxWidth: isMobile ? '90vw' : 280,
            borderRadius: isMobile ? 12 : 16,
            padding: isMobile ? 0 : 4,
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleCopy} style={{ fontSize: isMobile ? 15 : 16, minHeight: isMobile ? 36 : 44 }}>
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} />
          Copy Link
        </MenuItem>
        <MenuItem
          component="a"
          href={`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { notifyShared(); handleClose(); }}
          style={{ fontSize: isMobile ? 15 : 16, minHeight: isMobile ? 36 : 44 }}
        >
          <WhatsAppIcon fontSize="small" style={{ marginRight: 8, color: '#25D366' }} />
          WhatsApp
        </MenuItem>
        <MenuItem
          component="a"
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { notifyShared(); handleClose(); }}
          style={{ fontSize: isMobile ? 15 : 16, minHeight: isMobile ? 36 : 44 }}
        >
          <TwitterIcon fontSize="small" style={{ marginRight: 8, color: '#1DA1F2' }} />
          X (Twitter)
        </MenuItem>
        <MenuItem
          component="a"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { notifyShared(); handleClose(); }}
          style={{ fontSize: isMobile ? 15 : 16, minHeight: isMobile ? 36 : 44 }}
        >
          <FacebookIcon fontSize="small" style={{ marginRight: 8, color: '#4267B2' }} />
          Facebook
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={handleSnackbarClose}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          style: {
            borderRadius: 14,
            background: 'rgba(50,50,50,0.9)',
            fontWeight: 500,
            fontSize: 14,
            padding: '8px 20px',
          },
        }}
      />

      {/* Checkmark bounce animation style */}
      <style>
        {`
          @keyframes bounce {
            0% { transform: scale(0.8); opacity: 0; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default ShareMenu;
